import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { errorMessage, unwrap } from 'api/call'
import {
  deleteUsersById,
  getUsersAdvancedSearchFilters,
  getUsersByIdAdminDetails,
  patchUsersById,
  postUsersAdvancedSearch,
  postUsersAccountConfirmationEmail,
  putResetPasswordRequest,
  putUsersByIdBan,
  putUsersByIdMarkAsHiddenResults,
  putUsersByIdMarkAsPremiumUser,
  putUsersByIdSetPremiumExpirationDate,
  putUsersByIdUnban,
  type BackofficeUser,
  type UserAdminDetailsResponse,
  type UserAdvancedSearchFilters,
  type UserAdvancedSearchResponse,
  type UserAdvancedSearchResult,
  type UserAdvancedSearchSummary,
  type UserUpdateResponse,
} from 'api/generated'
import { toast } from 'sonner'
import type { UserSearchInfo, UserSearchParams } from 'types/users'

export type UserRow = BackofficeUser & {
  /** Advanced-search data of the row (segment, FIT card, last activity...). */
  search: UserSearchInfo
  loaders: {
    isBanned: boolean
    hiddenResults: boolean
    premiumUser: boolean
    premiumExpirationDate: boolean
  }
}

type UserLoader = keyof UserRow['loaders']

interface UsersState {
  loading: boolean
  error: string | null
  users: UserRow[] | null
  page: number
  totalPages: number
  totalCount: number
  /** Counts over the whole filtered set, not just the page. */
  summary: UserAdvancedSearchSummary | null
  sendMailLoading: boolean
  /** Option lists of the filters (client versions in use...), loaded once. */
  filterOptions: UserAdvancedSearchFilters | null
  /** Admin card of the user open in the dialog, loaded when it opens. */
  details: {
    userId: number | null
    data: UserAdminDetailsResponse | null
    loading: boolean
    error: string | null
  }
}

const initialState: UsersState = {
  loading: false,
  error: null,
  users: null,
  page: 1,
  totalPages: 0,
  totalCount: 0,
  summary: null,
  sendMailLoading: false,
  filterOptions: null,
  details: { userId: null, data: null, loading: false, error: null },
}

// The backoffice is admin-only, so `backoffice_user` is always there; the guard
// only skips rows of a non-admin caller.
const toRows = (results: UserAdvancedSearchResult[]): UserRow[] =>
  results.flatMap(({ backoffice_user, ...search }) =>
    backoffice_user
      ? [
          {
            ...backoffice_user,
            search,
            loaders: {
              isBanned: false,
              hiddenResults: false,
              premiumUser: false,
              premiumExpirationDate: false,
            },
          },
        ]
      : []
  )

/** Applies `changes` (and optionally a loader flag) to the user with `id`. */
const patchUser = (
  state: UsersState,
  id: number,
  changes: (user: UserRow) => Partial<BackofficeUser>,
  loader?: [UserLoader, boolean]
) => {
  if (!state.users) return
  state.users = state.users.map((user) =>
    user.id === id
      ? {
          ...user,
          ...changes(user),
          loaders: loader
            ? { ...user.loaders, [loader[0]]: loader[1] }
            : user.loaders,
        }
      : user
  )
}

// Async Thunks
export const searchUsers = createAsyncThunk<
  UserAdvancedSearchResponse,
  UserSearchParams,
  { rejectValue: string }
>('users/searchUsers', async (params, { rejectWithValue }) => {
  try {
    return await unwrap(postUsersAdvancedSearch({ body: params }))
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Ricerca utenti fallita.'))
  }
})

export const fetchUserFilterOptions = createAsyncThunk<
  UserAdvancedSearchFilters,
  void,
  { rejectValue: string }
>('users/fetchFilterOptions', async (_, { rejectWithValue }) => {
  try {
    return await unwrap(getUsersAdvancedSearchFilters())
  } catch (error) {
    return rejectWithValue(
      errorMessage(error, 'Opzioni dei filtri non caricate.')
    )
  }
})

export const fetchUserDetails = createAsyncThunk<
  UserAdminDetailsResponse,
  number,
  { rejectValue: string }
>('users/fetchDetails', async (userId, { rejectWithValue }) => {
  try {
    return await unwrap(
      getUsersByIdAdminDetails({ path: { id: String(userId) } })
    )
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Scheda utente non caricata.'))
  }
})

export const setHiddenResults = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('users/setHiddenResults', async (userId, { rejectWithValue }) => {
  try {
    await unwrap(
      putUsersByIdMarkAsHiddenResults({ path: { id: String(userId) } })
    )
    return userId
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to set hidden results.'))
  }
})

export const setPremiumUser = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('users/setPremiumUser', async (userId, { rejectWithValue }) => {
  try {
    await unwrap(
      putUsersByIdMarkAsPremiumUser({ path: { id: String(userId) } })
    )
    return userId
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to set premium user.'))
  }
})

export const banUser = createAsyncThunk<
  { userId: number; notes: string },
  { userId: number; notes: string },
  { rejectValue: string }
>('users/ban', async ({ userId, notes }, { rejectWithValue }) => {
  try {
    await unwrap(
      putUsersByIdBan({
        path: { id: String(userId) },
        query: { ban_notes: notes },
      })
    )
    return { userId, notes }
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to ban user.'))
  }
})

export const unbanUser = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('users/unban', async (userId, { rejectWithValue }) => {
  try {
    await unwrap(putUsersByIdUnban({ path: { id: String(userId) } }))
    return userId
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to unban user.'))
  }
})

export const setPremiumExpirationDate = createAsyncThunk<
  { userId: number; date: string },
  { userId: number; date: string },
  { rejectValue: string }
>(
  'users/setPremiumExpirationDate',
  async ({ userId, date }, { rejectWithValue }) => {
    try {
      const iso = new Date(date).toISOString()
      await unwrap(
        putUsersByIdSetPremiumExpirationDate({
          path: { id: String(userId) },
          query: { date: iso },
        })
      )
      return { userId, date: iso }
    } catch (error) {
      return rejectWithValue(
        errorMessage(error, 'Failed to set premium expiration date.')
      )
    }
  }
)

export const sendConfirmationMail = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('users/sendConfirmationMail', async (email, { rejectWithValue }) => {
  try {
    await unwrap(postUsersAccountConfirmationEmail({ query: { email } }))
    return email
  } catch (error) {
    return rejectWithValue(
      errorMessage(error, 'Failed to set send again confirmation email.')
    )
  }
})

export const requestPasswordReset = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('users/requestPasswordReset', async (email, { rejectWithValue }) => {
  try {
    await unwrap(putResetPasswordRequest({ body: { email } }))
    return email
  } catch (error) {
    return rejectWithValue(
      errorMessage(error, 'Failed to request password reset.')
    )
  }
})

export const deleteUser = createAsyncThunk<
  { success: boolean },
  number,
  { rejectValue: string }
>('users/deleteUser', async (id, { dispatch, rejectWithValue }) => {
  try {
    const response = await unwrap(deleteUsersById({ path: { id: String(id) } }))
    toast.success('User eliminato con successo')
    dispatch(userDeleted(id))
    return response
  } catch (error) {
    toast.error("Impossibile eseguire l'operazione.")
    return rejectWithValue(errorMessage(error, 'Failed to delete user.'))
  }
})

export const updateUser = createAsyncThunk<
  UserUpdateResponse,
  {
    userId: number
    data: { email: string; first_name: string; last_name: string }
  },
  { rejectValue: string }
>('users/updateUser', async ({ userId, data }, { rejectWithValue }) => {
  try {
    return await unwrap(
      patchUsersById({ path: { id: String(userId) }, body: { user: data } })
    )
  } catch (error) {
    toast.error("Impossibile eseguire l'operazione.")
    return rejectWithValue(errorMessage(error, 'Failed to update user.'))
  }
})

// Slice
const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    userDeleted: (state, action: PayloadAction<number>) => {
      if (state.users) {
        state.users = state.users.filter((user) => user.id !== action.payload)
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchUsers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(searchUsers.fulfilled, (state, action) => {
        state.loading = false
        state.totalPages = action.payload.total_pages
        state.totalCount = action.payload.total_count
        state.page = action.payload.page
        state.summary = action.payload.summary
        state.users = toRows(action.payload.users)
      })
      .addCase(searchUsers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Ricerca utenti fallita.'
      })
      .addCase(fetchUserFilterOptions.fulfilled, (state, action) => {
        state.filterOptions = action.payload
      })
      .addCase(fetchUserDetails.pending, (state, action) => {
        state.details = {
          userId: action.meta.arg,
          data: null,
          loading: true,
          error: null,
        }
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        // a slower answer for a user closed in the meantime is dropped
        if (state.details.userId !== action.meta.arg) return
        state.details.loading = false
        state.details.data = action.payload
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        if (state.details.userId !== action.meta.arg) return
        state.details.loading = false
        state.details.error = action.payload || 'Scheda utente non caricata.'
      })
      .addCase(setHiddenResults.pending, (state, action) => {
        patchUser(state, action.meta.arg, () => ({}), ['hiddenResults', true])
        state.error = null
      })
      .addCase(setHiddenResults.fulfilled, (state, action) => {
        state.loading = false
        patchUser(
          state,
          action.payload,
          (user) => ({ hidden_results: !user.hidden_results }),
          ['hiddenResults', false]
        )
      })
      .addCase(setHiddenResults.rejected, (state, action) => {
        state.loading = false
        toast.error(action.payload || 'Failed to set hidden results.')
        patchUser(state, action.meta.arg, () => ({}), ['hiddenResults', false])
      })
      .addCase(setPremiumUser.pending, (state, action) => {
        patchUser(state, action.meta.arg, () => ({}), ['premiumUser', true])
        state.error = null
      })
      .addCase(setPremiumUser.fulfilled, (state, action) => {
        state.loading = false
        patchUser(
          state,
          action.payload,
          (user) => ({ premium: !user.premium }),
          ['premiumUser', false]
        )
        const user = state.users?.find(({ id }) => id === action.payload)
        if (user)
          toast.success(user.premium ? 'Utente premium' : 'Premium rimosso')
      })
      .addCase(setPremiumUser.rejected, (state, action) => {
        state.loading = false
        toast.error('Impossibile cambiare lo stato premium.')
        patchUser(state, action.meta.arg, () => ({}), ['premiumUser', false])
      })
      .addCase(sendConfirmationMail.pending, (state) => {
        state.sendMailLoading = true
        state.error = null
      })
      .addCase(sendConfirmationMail.fulfilled, (state) => {
        state.sendMailLoading = false
        toast.success('Email di conferma account inviata con successo')
      })
      .addCase(sendConfirmationMail.rejected, (state) => {
        state.sendMailLoading = false
        toast.error(
          "Si è verificato un errore durante l'invio dell'email di conferma account"
        )
      })
      .addCase(requestPasswordReset.pending, (state) => {
        state.sendMailLoading = true
        state.error = null
      })
      .addCase(requestPasswordReset.fulfilled, (state) => {
        state.sendMailLoading = false
        toast.success('Email per il cambio password inviata con successo')
      })
      .addCase(requestPasswordReset.rejected, (state) => {
        state.sendMailLoading = false
        toast.error(
          "Si è verificato un errore durante l'invio dell'email per il cambio password"
        )
      })
      .addCase(banUser.pending, (state, action) => {
        patchUser(state, action.meta.arg.userId, () => ({}), ['isBanned', true])
        state.error = null
      })
      .addCase(banUser.fulfilled, (state, action) => {
        state.loading = false
        patchUser(
          state,
          action.payload.userId,
          () => ({ is_banned: true, ban_notes: action.payload.notes }),
          ['isBanned', false]
        )
      })
      .addCase(banUser.rejected, (state, action) => {
        state.loading = false
        toast.error("Impossibile bannare l'utente.")
        patchUser(state, action.meta.arg.userId, () => ({}), [
          'isBanned',
          false,
        ])
      })
      .addCase(unbanUser.pending, (state, action) => {
        patchUser(state, action.meta.arg, () => ({}), ['isBanned', true])
        state.error = null
      })
      .addCase(unbanUser.fulfilled, (state, action) => {
        state.loading = false
        patchUser(
          state,
          action.payload,
          () => ({ is_banned: false, ban_notes: null }),
          ['isBanned', false]
        )
      })
      .addCase(unbanUser.rejected, (state, action) => {
        state.loading = false
        toast.error('Impossibile rimuovere il ban.')
        patchUser(state, action.meta.arg, () => ({}), ['isBanned', false])
      })
      .addCase(updateUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false
        toast.success('User modificato con successo')
        // The PATCH answers a raw UserRecord (not a BackofficeUser): merge only
        // the fields the edit dialog can change.
        const { id, email, first_name, last_name, full_name } =
          action.payload.user
        patchUser(state, id, (user) => ({
          email,
          first_name,
          last_name,
          full_name: full_name ?? user.full_name,
        }))
      })
      .addCase(updateUser.rejected, (state) => {
        state.loading = false
      })
      .addCase(setPremiumExpirationDate.pending, (state, action) => {
        patchUser(state, action.meta.arg.userId, () => ({}), [
          'premiumExpirationDate',
          true,
        ])
        state.error = null
      })
      .addCase(setPremiumExpirationDate.fulfilled, (state, action) => {
        state.loading = false
        patchUser(
          state,
          action.payload.userId,
          () => ({ premium_expiration: action.payload.date }),
          ['premiumExpirationDate', false]
        )
        toast.success('Scadenza premium salvata')
      })
      .addCase(setPremiumExpirationDate.rejected, (state, action) => {
        state.loading = false
        toast.error('Impossibile salvare la scadenza premium.')
        patchUser(state, action.meta.arg.userId, () => ({}), [
          'premiumExpirationDate',
          false,
        ])
      })
  },
})

export const { userDeleted } = usersSlice.actions

export const usersReducer = usersSlice.reducer
